/**
 * Shared stats text generation for CustomStats.
 * Used by CustomStats (interactive: from SearchContext) and by buildSrpViewModel (static: pre-computed strings).
 * Keeps display stable; only the strings change when the count/query/filters update.
 */

export type StatsLocale = 'en' | 'fi' | 'sv';

export interface GenerateStatsTextInput {
  nbHits: number;
  filters: Record<string, string[]>;
  query: string;
  locale: StatsLocale;
}

const parsedFiltersFrom = (filters: Record<string, string[]>) => ({
  company: filters.companyId?.[0] || '',
  industry: filters.companyIndustry?.[0] || '',
  location: filters.location?.[0] || '',
  language: filters.requiredLanguages?.[0] || '',
  jobTitle: filters.normalisedTitle?.[0] || '',
});

/**
 * Generates the stats headline string (e.g. "1,234 jobs in Finland", "42 Developer jobs in Helsinki").
 */
export function generateStatsText(input: GenerateStatsTextInput): string {
  const { nbHits, filters, query, locale } = input;
  const parsedFilters = parsedFiltersFrom(filters);
  const searchQuery = typeof query === 'string' ? query.trim() : '';

  const localeString = locale === 'fi' ? 'fi-FI' : locale === 'sv' ? 'sv-SE' : 'en-US';
  const countText = nbHits.toLocaleString(localeString);

  const cityName = parsedFilters.location
    ? parsedFilters.location.includes(',')
      ? parsedFilters.location.split(',')[0]
      : parsedFilters.location
    : '';

  if (nbHits === 0) {
    if (locale === 'fi') return 'Tuloksia ei löytynyt';
    if (locale === 'sv') return 'Inga resultat hittades';
    return 'No results found';
  }

  if (locale === 'fi') {
    const jobWord = nbHits === 1 ? 'työpaikka' : 'työpaikkaa';
    const titlePart = parsedFilters.jobTitle ? `${parsedFilters.jobTitle}-` : '';

    if (parsedFilters.company && parsedFilters.jobTitle) {
      const locationPart = cityName ? ` ${cityName}ssa` : '';
      return `${countText} ${titlePart}${jobWord} ${parsedFilters.company}ssa${locationPart}`;
    }
    if (parsedFilters.company && parsedFilters.industry) {
      const locationPart = cityName ? ` ${cityName}ssa` : '';
      return `${countText} ${parsedFilters.industry}${locationPart} ${jobWord} ${parsedFilters.company}ssa`;
    }
    if (parsedFilters.company && cityName) {
      return `${countText} ${jobWord} ${parsedFilters.company}ssa ${cityName}ssa`;
    }
    if (parsedFilters.jobTitle && cityName) {
      return `${countText} ${titlePart}${jobWord} ${cityName}ssa`;
    }
    if (parsedFilters.industry && cityName) {
      return `${countText} ${parsedFilters.industry}-${jobWord} ${cityName}ssa`;
    }
    if (parsedFilters.jobTitle) {
      return `${countText} ${titlePart}${jobWord} Suomessa`;
    }
    if (parsedFilters.industry) {
      return `${countText} ${parsedFilters.industry}-${jobWord} Suomessa`;
    }
    if (cityName) {
      return `${countText} ${jobWord} ${cityName}ssa`;
    }
    if (parsedFilters.company) {
      return `${countText} ${jobWord} ${parsedFilters.company}ssa`;
    }
    if (searchQuery) {
      return `${countText} "${searchQuery}" ${jobWord} Suomessa`;
    }
    return `${countText} ${jobWord} Suomessa`;
  }

  if (locale === 'sv') {
    const jobWord = 'jobb';

    if (parsedFilters.company && parsedFilters.jobTitle) {
      const locationPart = cityName ? ` i ${cityName}` : '';
      return `${countText} ${parsedFilters.jobTitle}-${jobWord} på ${parsedFilters.company}${locationPart}`;
    }
    if (parsedFilters.company && parsedFilters.industry) {
      const locationPart = cityName ? ` i ${cityName}` : '';
      return `${countText} ${parsedFilters.industry}-${jobWord} på ${parsedFilters.company}${locationPart}`;
    }
    if (parsedFilters.company && cityName) {
      return `${countText} ${jobWord} på ${parsedFilters.company} i ${cityName}`;
    }
    if (parsedFilters.jobTitle && cityName) {
      return `${countText} ${parsedFilters.jobTitle}-${jobWord} i ${cityName}`;
    }
    if (parsedFilters.industry && cityName) {
      return `${countText} ${parsedFilters.industry}-${jobWord} i ${cityName}`;
    }
    if (parsedFilters.jobTitle) {
      return `${countText} ${parsedFilters.jobTitle}-${jobWord} i Finland`;
    }
    if (parsedFilters.industry) {
      return `${countText} ${parsedFilters.industry}-${jobWord} i Finland`;
    }
    if (cityName) {
      return `${countText} ${jobWord} i ${cityName}`;
    }
    if (parsedFilters.company) {
      return `${countText} ${jobWord} på ${parsedFilters.company}`;
    }
    if (searchQuery) {
      return `${countText} "${searchQuery}" ${jobWord} i Finland`;
    }
    return `${countText} ${jobWord} i Finland`;
  }

  // English
  const jobWord = nbHits === 1 ? 'job' : 'jobs';

  if (parsedFilters.company && parsedFilters.jobTitle) {
    const locationPart = cityName ? ` in ${cityName}` : '';
    return `${countText} ${parsedFilters.jobTitle} ${jobWord} at ${parsedFilters.company}${locationPart}`;
  }
  if (parsedFilters.company && parsedFilters.industry) {
    const locationPart = cityName ? ` in ${cityName}` : '';
    return `${countText} ${parsedFilters.industry} ${jobWord} at ${parsedFilters.company}${locationPart}`;
  }
  if (parsedFilters.company && cityName) {
    return `${countText} ${jobWord} at ${parsedFilters.company} in ${cityName}`;
  }
  if (parsedFilters.jobTitle && cityName) {
    return `${countText} ${parsedFilters.jobTitle} ${jobWord} in ${cityName}`;
  }
  if (parsedFilters.industry && cityName) {
    return `${countText} ${parsedFilters.industry} ${jobWord} in ${cityName}`;
  }
  if (parsedFilters.jobTitle) {
    return `${countText} ${parsedFilters.jobTitle} ${jobWord} in Finland`;
  }
  if (parsedFilters.industry) {
    return `${countText} ${parsedFilters.industry} ${jobWord} in Finland`;
  }
  if (cityName) {
    return `${countText} ${jobWord} in ${cityName}`;
  }
  if (parsedFilters.company) {
    return `${countText} ${jobWord} at ${parsedFilters.company}`;
  }
  if (searchQuery) {
    return `${countText} "${searchQuery}" ${jobWord} in Finland`;
  }
  return `${countText} ${jobWord} in Finland`;
}

/**
 * Returns true when the stats text is the default "X jobs in Finland" (no filters, no query),
 * so the Finland subtitle should be shown.
 */
export function showFinlandSubtitle(
  statsText: string,
  filters: Record<string, string[]>,
  query: string
): boolean {
  const parsed = parsedFiltersFrom(filters);
  const hasFilters =
    parsed.company ||
    parsed.industry ||
    parsed.location ||
    parsed.language ||
    parsed.jobTitle ||
    (typeof query === 'string' && query.trim().length > 0);
  if (hasFilters) return false;
  return (
    statsText.includes('jobs in Finland') ||
    statsText.includes('työpaikkaa Suomessa') ||
    statsText.includes('jobb i Finland')
  );
}
